import {
  Box,
  Button,
  Dialog,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  addDays,
  areIntervalsOverlapping,
  differenceInCalendarDays,
  eachMonthOfInterval,
  endOfYear,
  format,
  getDaysInMonth,
  isAfter,
  isBefore,
  lastDayOfMonth,
  startOfYear,
} from "date-fns";
import { FC, useEffect, useRef, useState } from "react";
import {
  InventoryPlannedProducts,
  PlanTypes,
  Status,
} from "../../../../types/inventoryData";
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  CloseRounded,
  Edit,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import GanttEdit from "./GanttEdit";
import { useInventory } from "../../InventoryProvider";
import { UpdateInventoryPlan } from "../../../../api/inventory";
import { useApplicationContext } from "../../../ContextProviders/ApplicationContext";
import { ErrorCodes } from "../../../../types/enums";
import { useTranslation } from "react-i18next";

type GanttChartTypes = {
  open: boolean;
  handleClose: () => void;
};

const GanttChart: FC<GanttChartTypes> = ({ open, handleClose }) => {
  const [editPopup, setEditPopup] = useState<boolean>();
  const [editPlan, setEditPlan] = useState<PlanTypes>();
  const [date, setDate] = useState({
    start: new Date("2025-01-01"),
    end: new Date("2025-12-31"),
  });

  const [monthLabels, setMonthLabels] = useState<
    {
      name: string;
      span: number;
    }[]
  >([]);

  const [totalDays, setTotalDays] = useState<number>(0);
  const [daysOfNegativeStock, setDaysOfNegativeStock] = useState<
    {
      products: InventoryPlannedProducts[];
      start: Date;
      end: Date | null;
    }[]
  >();

  const { plans, setPlans, statusCounts, productTypes } = useInventory();
  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();

  useEffect(() => {
    //There is one issue with how it works right now.
    //If for example the last project has 30 ElowHubs but only 21 are available
    //It will display that there is a shortage for the whole period until no products are in a shortage
    //But only the vest is in a shortage for the whole range. ElbowHub is only shorted for that one project time.
    //Meaning the area marked goes from 5.June to 25.August
    //But only Elbowhub is shorted 5.June to 15.Aug and vest is shorted from 5.June to 25.August.

    //---
    //There is also a issue with calculating based on the current availablity. If we send something to a customer
    //as we have written in the chart, it will says we dont have enough available for that customer.
    //So we need to remove that customer/project from the calculations or somehow link the inventory to the gantt chart.
    //Otherwise we calculate 2x the project, once when we send it and they actually get in use and then when we check the gantt chart.

    // Chat GPT Explanation?
    // Issue: When calculating availability across all projects, the system
    // subtracts the required items for every project from current inventory.
    // If we’ve already sent items to a customer (e.g., project A),
    // that project should be excluded from the calculation.
    // Otherwise, the calculation treats the items as still needed,
    // leading to double counting.
    const startDates = plans.map((plan) => plan.startDate);
    const endDates = plans.map((plan) => plan.endDate);
    const allDates = [...startDates, ...endDates];
    const allDatesSorted = allDates.sort((a: any, b: any) => a - b);

    const testingDays = allDatesSorted.slice(0, 1);

    const startAndEndDates: {
      products: InventoryPlannedProducts[];
      start: Date;
      end: Date | null;
    }[] = [];

    let index: number = -1;
    let added: boolean = false;

    allDatesSorted.forEach((date) => {
      const overlapping = plans.filter((plan) => {
        return areIntervalsOverlapping(
          { start: new Date(date), end: addDays(new Date(date), 1) },
          { start: new Date(plan.startDate), end: new Date(plan.endDate) }
        );
      });

      // if (overlapping.length <= 1) return;

      const overlappingPlannedProducts = overlapping.map(
        (p) => p.plannedProducts
      );

      // let obj: Record<ProductTypes, number> = { ...statusCounts[Status.Available] };
      let arr: InventoryPlannedProducts[] = [];
      // Object.entries(obj).map(item => {
      //   arr.push({productType: item[0] as ProductTypes, quantity: item[1]})
      //   return arr;
      // })

      Object.entries(statusCounts[Status.Available]).map(([key, value]) => {
        const productTypeFound = productTypes.find((p) => p.name === key);
        if (productTypeFound)
          arr.push({ productType: productTypeFound, quantity: value });
        else return;
      });

      overlappingPlannedProducts.forEach((singlePlannedProduct) => {
        // console.log("singlePlannedProduct", singlePlannedProduct);

        singlePlannedProduct.map((item) => {
          // console.log(item.productType);

          const foundItem = arr.find((i) => i.productType == item.productType);
          if (foundItem) {
            const index = arr.findIndex(
              (i) => i.productType == item.productType
            );
            const changedValues: InventoryPlannedProducts = {
              productType: item.productType,
              quantity: foundItem.quantity - item.quantity,
            };
            arr[index] = changedValues;
          }
        });

        // for (const key in singlePlannedProduct) {
        // console.log("key", key);

        // const found = singlePlannedProduct.find(product => product.productType.includes(key as ProductTypes))
        // console.log(found);

        // obj = {
        //   ...obj,
        //   [key as ProductTypes]:
        //     obj[key as ProductTypes] - i[key as ProductTypes],
        // };
        // }
      });

      if (arr.some((v) => v.quantity < 0)) {
        if (added !== true) {
          const onlyNegs = arr.filter((item) => item.quantity < 0);
          // const onlyNegative = Object.fromEntries(
          //   Object.entries(obj).filter(([, v]) => v < 0)
          // ) as Record<ProductTypes, number>;
          startAndEndDates.push({
            products: onlyNegs,
            start: date,
            end: null,
          });
          index += 1;
          added = true;
          // console.log("HIGHER ADDED", format(date, "dd-MM-yyyy"));
          return;
        }
        // console.log("HIGHER NOT ADDED", format(date, "dd-MM-yyyy"));
        return;
      } else {
        if (index > -1 && added !== false) {
          startAndEndDates[index].end = date;
          added = false;
          // console.log("LOWER ADDED", format(date, "dd-MM-yyyy"));
          return;
        }
        // console.log("LOWER NOT ADDED", format(date, "dd-MM-yyyy"));
        return;
      }
    });

    setDaysOfNegativeStock(startAndEndDates);
  }, []);

  useEffect(() => {
    generateTimelineData();
  }, [date]);

  const generateTimelineData = () => {
    const eachMonth = eachMonthOfInterval({
      start: date.start,
      end: date.end,
    });

    let monthLabel: {
      name: string;
      span: number;
    }[] = [];

    for (const month of eachMonth) {
      const thisMonth = format(month, "yyyy-MMM");
      const firstMonth = format(eachMonth[0], "yyyy-MMM");
      const lastMonth = format(eachMonth[eachMonth.length - 1], "yyyy-MMM");
      let daysThisMonth = getDaysInMonth(month);
      let monthName = format(month, "MMM");

      if (thisMonth === firstMonth) {
        daysThisMonth -= differenceInCalendarDays(new Date(date.start), month);
      } else if (thisMonth === lastMonth) {
        daysThisMonth -= differenceInCalendarDays(
          lastDayOfMonth(month),
          new Date(date.end)
        );
      }

      if (month.getMonth() == 0) monthName = format(month, "MMM ’yy");

      monthLabel.push({
        name: monthName,
        span: daysThisMonth,
      });
    }
    setMonthLabels(monthLabel);
    setTotalDays(monthLabel.reduce((acc, cur) => acc + cur.span, 0));
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const scrollAmount = 7;

    const direction = e.deltaY > 0 ? scrollAmount : -scrollAmount;
    setDate((prev) => ({
      ...prev,
      start: addDays(prev.start, direction),
      end: addDays(prev.end, direction),
    }));
  };

  const borderStyling = "1px solid hsla(0, 0%, 30%, .25)";

  const DatePickerComp: FC<{ label: any; item: any; value: any }> = ({
    label,
    value,
    item,
  }) => {
    return (
      <DatePicker
        value={value}
        onChange={(newValue) =>
          setDate((prev) => ({ ...prev, [item]: newValue }))
        }
        label={label}
        showDaysOutsideCurrentMonth
        displayWeekNumber
      />
    );
  };

  const [hoverDate, setHoverDate] = useState<Date>();
  const [cursorX, setCursorX] = useState<number>();
  const [cursorY, setCursorY] = useState<number>();
  const gridRef = useRef<HTMLDivElement>(null);

  const handleDragEnd = async (
    event: any,
    plan: PlanTypes,
    startOrEnd: string
  ) => {
    if (!gridRef.current) return;

    const rect = gridRef.current.getBoundingClientRect();
    const xInsideGrid = event.clientX - rect.left;
    const fraction = xInsideGrid / rect.width;
    const clampedFraction = Math.min(Math.max(fraction, 0), 1);
    const dayIndex = Math.floor(clampedFraction * totalDays);
    const selectedDate = addDays(new Date(date.start), dayIndex);
    console.log("Dropped at date:", selectedDate);

    let editedPlan: PlanTypes = structuredClone(plan);
    if (startOrEnd == "start") {
      editedPlan = { ...plan, startDate: selectedDate };
    }
    if (startOrEnd == "end") {
      editedPlan = { ...plan, endDate: selectedDate };
    }

    try {
      const updatedPlan: PlanTypes = await UpdateInventoryPlan(editedPlan);
      const changedPlans = plans.map((p) =>
        p.inventoryPlanId == updatedPlan.inventoryPlanId ? updatedPlan : p
      );
      setPlans(changedPlans);
      showSnackbar(t("inventory.successupdate"), "success");
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case ErrorCodes.EmptyPlannedProductsOnPlan:
            showSnackbar(t("errors.EmptyPlannedProductsOnPlan"), "error");
            break;
          case ErrorCodes.PlanNotFound:
            showSnackbar(t("errors.PlanNotFound"), "error");
            break;
          case ErrorCodes.InternalServerError:
            showSnackbar(t("errors.UnexpectedError"), "error");
        }
      }
      showSnackbar(t("errors.UnexpectedError"), "error");
    }
  };

  // const handleAddProject = () => {
  //   const id = projects.length + 1;
  //   const today = new Date(Date.now());

  //   const newProject: ProjectTypes = {
  //     id: id,
  //     companyName: "",
  //     startDate: today,
  //     endDate: today,
  //     products: {
  //       BackHub: 0,
  //       BackVest: 0,
  //       ElbowHub: 0,
  //       ElbowSleeve: 0,
  //     },
  //     allocatedProducts: {
  //       BackHub: [],
  //       BackVest: [],
  //       ElbowHub: [],
  //       ElbowSleeve: [],
  //     },
  //   };

  //   setProjects((prev) => [...prev, newProject]);
  //   setEditPopup(true);
  //   setEditProject(newProject);
  // };

  return (
    <>
      {editPopup && (
        <GanttEdit
          statusCounts={statusCounts}
          editPopup={editPopup}
          handleClose={() => {
            setEditPlan(undefined);
            setEditPopup(false);
          }}
          editPlan={editPlan}
        />
      )}
      <Dialog open={open} onClose={handleClose} fullScreen>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box display="flex" justifyContent="space-between" sx={{ p: 2 }}>
            <Box display="flex" width="fit-content" gap={2}>
              <DatePickerComp
                label={t("inventory.startDate")}
                value={date.start}
                item="start"
              />
              <DatePickerComp
                label={t("inventory.endDate")}
                value={date.end}
                item="end"
              />
              <Button
                variant="contained"
                onClick={() => {
                  setDate(() => {
                    const firstDayOfYear = startOfYear(Date.now());
                    const lastDayOfYear = endOfYear(Date.now());
                    return {
                      start: firstDayOfYear,
                      end: lastDayOfYear,
                    };
                  });
                }}
              >
                {t("inventory.thisYear")}
              </Button>
              <Button variant="contained" onClick={() => setEditPopup(true)}>
                {t("inventory.addPlan")}
              </Button>
            </Box>
            <Box>
              <IconButton size="large" onClick={handleClose}>
                <CloseRounded />
              </IconButton>
            </Box>
          </Box>
        </LocalizationProvider>

        <Box
          display="grid"
          gridTemplateColumns={"auto 1fr"}
          gridTemplateRows={"auto auto"}
        >
          {/* Left Column - Empty Cell */}
          <Box
            gridColumn={"1 / 2"}
            gridRow={"1 / 2"}
            borderBottom={borderStyling}
            borderRight={borderStyling}
            p={2}
          >
            <Typography>{t("inventory.Plans")}</Typography>
          </Box>

          {/* Left Column - Plans */}
          <Box gridColumn={"1 / 2"} gridRow={"2 / -1"}>
            {plans.map((p, index) => (
              <Box
                key={index}
                pl={2}
                pr={2}
                pt={1}
                pb={1}
                borderBottom={borderStyling}
                borderRight={borderStyling}
                gap={1}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                bgcolor={
                  index % 2 == 0 ? "hsl(0, 0%, 100%)" : "hsl(220, 16%, 96%)"
                }
              >
                <Box display={"flex"} flexDirection={"column"}>
                  <Typography variant="body1">{p.companyName}</Typography>
                  <Typography variant="caption" color={"GrayText"}>
                    {format(new Date(p.startDate), "dd-MMM/yy")} -{" "}
                    {format(new Date(p.endDate), "dd-MMM/yy")}
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => {
                    setEditPopup(true);
                    setEditPlan(p);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          {/* Border below Month Labels */}
          <Box
            gridColumn={"2 / -1"}
            gridRow={"1 / 2"}
            borderBottom={borderStyling}
          ></Box>

          {/* Right Column - MonthLabels */}
          <Box
            ref={gridRef}
            onWheel={handleWheel}
            display={"grid"}
            gridTemplateColumns={`repeat(${totalDays}, 1fr)`}
            gridColumn={"2 / -1"}
            gridRow={"1 / -1"}
            zIndex={5}
          >
            {monthLabels.map((month, index) => (
              // MonthLabel
              <Box
                key={index}
                gridColumn={`span ${month.span}`}
                height={"100%"}
                borderRight={borderStyling}
                overflow={"hidden"}
              >
                <Box pt={2} pb={2} bgcolor={"hsl(220, 16%, 96%)"}>
                  <Typography pl={1} sx={{ textWrap: "nowrap" }}>
                    {month.name}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Right Column - Timeline */}
          <Box
            display={"grid"}
            gridTemplateRows={`repeat(${plans.length}, 1fr)`}
            gridTemplateColumns={"1fr"}
            gridColumn={"2 / -1"}
            gridRow={"2 / -1"}
            overflow={"hidden"}
          >
            {plans.map((plan, index) => {
              const durationInDays =
                differenceInCalendarDays(
                  new Date(plan.endDate) < new Date(date.end)
                    ? new Date(plan.endDate)
                    : new Date(date.end),
                  new Date(plan.startDate) > new Date(date.start)
                    ? new Date(plan.startDate)
                    : new Date(date.start)
                ) + 1;

              const offset = Math.max(
                0,
                differenceInCalendarDays(
                  new Date(plan.startDate),
                  new Date(date.start)
                )
              );

              const allocated = plan.allocatedProducts;

              if (allocated == null) return;

              const supermap = new Map<string, number>();

              productTypes.forEach((e) => {
                supermap.set(e.name, 0);
              });

              allocated.forEach((i) => {
                let value = supermap.get(i.productType.name);
                if (value == undefined) value = 0;
                supermap.set(i.productType.name, value + 1);
              });

              return (
                <Box
                  onWheel={handleWheel}
                  key={`timeline.${index}`}
                  // display={"grid"}
                  // gridTemplateColumns={`repeat(${totalDays}, 1fr)`}
                  // gridColumn={`span ${totalDays}`}
                  borderBottom={borderStyling}
                  position={"relative"}
                  display={"flex"}
                  bgcolor={
                    index % 2 == 0 ? "hsl(0, 0%, 100%)" : "hsl(220, 16%, 96%)"
                  }
                >
                  {isBefore(new Date(plan.startDate), date.start) && (
                    <Box
                      position={"absolute"}
                      height={"100%"}
                      width={"min-content"}
                      z-index={"20"}
                      display={"flex"}
                      alignItems={"center"}
                      zIndex={30}
                    >
                      <ChevronLeftRounded key={`timeline.chevron.${index}`} />
                    </Box>
                  )}
                  {isAfter(new Date(plan.endDate), date.end) && (
                    <Box
                      position={"absolute"}
                      height={"100%"}
                      width={"min-content"}
                      z-index={"20"}
                      display={"flex"}
                      alignItems={"center"}
                      zIndex={30}
                      right={8}
                    >
                      <ChevronRightRounded key={`timeline.chevron.${index}`} />
                    </Box>
                  )}
                  <Tooltip
                    title={
                      <Box display="flex" gap={2}>
                        <span>
                          {t("inventory.planned")} <br />
                          {plan.plannedProducts.map((product, index) => (
                            <span key={index}>
                              {product.productType.name}: {product.quantity}
                              <br />
                            </span>
                          ))}
                        </span>
                        <span>
                          {t("inventory.allocated")} <br />
                          {productTypes.map((value) => (
                            <span key={value.productTypeId}>
                              {supermap.get(value.name)}
                              <br />
                            </span>
                          ))}
                        </span>
                      </Box>
                    }
                    followCursor
                  >
                    <Box
                      key={`timeline.color.${index}`}
                      // gridColumn={`${offset} / span ${durationInDays}`}
                      bgcolor={"cadetblue"}
                      height={"60%"}
                      alignSelf={"center"}
                      width={`calc(${durationInDays} / ${totalDays} * 100%)`}
                      left={`calc(${offset} / ${totalDays} * 100%)`}
                      position={"relative"}
                      borderRadius={1}
                      zIndex={20}
                      display={"flex"}
                      justifyContent={"space-between"}
                    >
                      <Box
                        bgcolor={"hsl(182, 25%, 60%)"}
                        width={"8px"}
                        m={`auto 4px`}
                        borderRadius={"100px"}
                        height={"80%"}
                        onDragEnd={(event) =>
                          handleDragEnd(event, plan, "start")
                        }
                        draggable
                      />
                      <Box
                        bgcolor={"hsl(182, 25%, 60%)"}
                        width={"8px"}
                        m={`auto 4px`}
                        borderRadius={"100px"}
                        height={"80%"}
                        onDragEnd={(event) => handleDragEnd(event, plan, "end")}
                        draggable
                      />
                      {hoverDate && (
                        <Box
                          position={"fixed"}
                          left={cursorX}
                          top={cursorY ? cursorY - 30 : 0}
                          bgcolor={"white"}
                          border={"1px solid grey"}
                          px={1}
                          borderRadius={1}
                          zIndex={1000}
                        >
                          {format(hoverDate, "MMM d")}
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>

          {/* Today Marker */}
          {isAfter(Date.now(), date.start) &&
            isBefore(Date.now(), date.end) && (
              <Box
                onWheel={handleWheel}
                display={"grid"}
                position={"relative"}
                width={"2px"}
                left={`calc(${differenceInCalendarDays(
                  Date.now(),
                  new Date(date.start)
                )} / ${totalDays} * 100%)`}
                gridColumn={"2 / 2"}
                gridRow={"2 / -1"}
                bgcolor={"red"}
                zIndex={30}
              />
            )}

          {/* Negative Stock Marker */}
          {daysOfNegativeStock &&
            daysOfNegativeStock.map((dates, index) => {
              if (dates.end == null) {
                return <></>;
              }
              const durationInDays = differenceInCalendarDays(
                dates.end < date.end ? dates.end : date.end,
                dates.start > date.start ? dates.start : date.start
              );
              const offset = Math.max(
                0,
                differenceInCalendarDays(
                  new Date(dates.start),
                  new Date(date.start)
                )
              );
              return (
                <Tooltip
                  key={index}
                  title={Object.entries(dates.products).map(([key, value]) => (
                    <span key={key}>
                      {value.productType.name}: {value.quantity}
                      <br />
                    </span>
                  ))}
                >
                  <Box
                    onWheel={handleWheel}
                    display={"grid"}
                    position={"relative"}
                    gridColumn={"2 / 2"}
                    gridRow={"2 / -1"}
                    bgcolor={"hsla(0, 100%, 50%, .20)"}
                    width={`calc(${durationInDays} / ${totalDays} * 100%)`}
                    left={`calc(${offset} / ${totalDays} * 100%)`}
                    zIndex={10}
                  />
                </Tooltip>
              );
            })}
        </Box>
      </Dialog>
    </>
  );
};

export default GanttChart;
